import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * SquareCardForm — Zita's in-app card entry via Square Web Payments SDK.
 *
 * Props:
 *   applicationId   Square Production Application ID (backend supplied)
 *   locationId      Square Production Location ID
 *   onToken         async (nonceToken, cardholderName) => any
 *                   Called after the browser tokenizes the card. Parent
 *                   is responsible for POST /api/billing/attach-card
 *                   + POST /api/billing/activate.
 *   submitLabel     Button label (default: "Save card & subscribe")
 *   cardholderName  Optional prefill for the cardholder name
 *   busy            External busy flag (disables submit)
 *
 * Loads the production Web Payments SDK from https://web.squarecdn.com.
 * Never emits raw card data — the Square SDK tokenizes it in an iframe
 * so PAN never touches Zita servers.
 */
const SQUARE_SDK_URL = 'https://web.squarecdn.com/v1/square.js';

let sdkLoadPromise = null;
const loadSdk = () => {
  if (window.Square) return Promise.resolve(window.Square);
  if (sdkLoadPromise) return sdkLoadPromise;
  sdkLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SQUARE_SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error('Square Web Payments SDK failed to load.'));
    document.head.appendChild(s);
  });
  return sdkLoadPromise;
};

export const SquareCardForm = ({
  applicationId,
  locationId,
  onToken,
  submitLabel = 'Save card & subscribe',
  cardholderName: initialName = '',
  busy = false,
}) => {
  const cardMountRef = useRef(null);
  const cardRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cardholderName, setCardholderName] = useState(initialName);

  useEffect(() => {
    let cancelled = false;

    if (!applicationId || !locationId) {
      setError('Square billing is not configured yet.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const Square = await loadSdk();
        if (cancelled) return;
        const payments = Square.payments(applicationId, locationId);
        const card = await payments.card({
          style: {
            input: {
              color: '#3b2a1e',
              fontSize: '16px',
            },
            '.input-container.is-focus': {
              borderColor: '#7b4a2b',
            },
            '.message-text': { color: '#a3341c' },
          },
        });
        if (cancelled) return;
        await card.attach(cardMountRef.current);
        cardRef.current = card;
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Could not initialize Square card entry.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch { /* no-op */ }
        cardRef.current = null;
      }
    };
  }, [applicationId, locationId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cardRef.current) return;
    setSubmitting(true);
    try {
      const result = await cardRef.current.tokenize({
        billingContact: cardholderName ? { givenName: cardholderName.split(' ')[0], familyName: cardholderName.split(' ').slice(1).join(' ') } : undefined,
        intent: 'STORE',
        customerInitiated: true,
        sellerKeyedIn: false,
      });
      if (result.status !== 'OK') {
        const msg = result.errors?.[0]?.message || 'Card was declined. Please try another card.';
        throw new Error(msg);
      }
      await onToken(result.token, cardholderName);
    } catch (err) {
      toast.error(err.message || 'Could not save your card. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="square-card-form">
      <div>
        <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1" htmlFor="sq-cardholder">
          Name on card
        </label>
        <input
          id="sq-cardholder"
          type="text"
          required
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Your full name"
          className="zita-input w-full pl-4 pr-4 py-3 text-sm"
          data-testid="square-cardholder"
        />
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">
          Card details
        </label>
        <div
          ref={cardMountRef}
          data-testid="square-card-mount"
          className="min-h-[56px] rounded-lg border border-zita-taupe/60 bg-white px-2 py-1"
        >
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zita-mocha py-4">
              <Loader2 size={14} className="animate-spin" /> Loading secure card form…
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2" data-testid="square-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || submitting || busy || !!error}
        data-testid="square-submit"
        className="zita-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {submitting || busy ? (
          <><Loader2 size={16} className="animate-spin" /> Processing…</>
        ) : (
          <><CreditCard size={16} /> {submitLabel}</>
        )}
      </button>

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-zita-muted">
        <ShieldCheck size={12} />
        Card details are tokenized directly by Square. Zita never sees your card number.
      </div>
    </form>
  );
};

export default SquareCardForm;
